<script setup lang="ts">
// @ts-expect-error Missing types
import type { Trigger } from 'vue-smart-suggest';
// @ts-expect-error Missing types
import { SmartSuggest } from 'vue-smart-suggest';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { computed, nextTick, onMounted, onUnmounted, reactive, useTemplateRef, watch } from 'vue';
import { Textarea } from '@/components/ui/textarea';
import {
    ArrowDownIcon,
    ArrowLeftIcon,
    ArrowRightIcon,
    ArrowUpIcon,
    ChevronUpIcon,
    CopyIcon,
    EllipsisIcon,
    PlusIcon,
    SaveIcon,
    Trash2Icon,
    XIcon,
    ClipboardCopyIcon,
    ChevronDownIcon,
    CheckIcon,
    InfoIcon,
} from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { router, ROUTES } from '@/router.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import AvailableVariablesDialog from '@/components/edit/AvailableVariablesDialog.vue';
import { MAX_PROMPTS_PER_PROFILE, MAX_STEPS_PER_PROFILE } from '@/constants/EditMaxes.ts';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn, truncateString } from '@/utils/RandomUtils.ts';
import { type FormatOption, FormatOptions } from '@/types/FormatOption.ts';
import NavHeader from '@/components/menu/NavHeader.vue';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { usePromptStore } from '@/stores/PromptStore.ts';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import copy from 'copy-to-clipboard';
import { ProfileConfigService } from '@/services/config/ProfileConfigService.ts';
import { EditLimitReachedError } from '@/errors/edit/EditLimitReachedError.ts';
import { StepConfigService } from '@/services/config/StepConfigService.ts';
import { showAlertDialogAsync } from '@/wrappers/ShowAlertDialogAsync.ts';
import { PromptConfigService } from '@/services/config/PromptConfigService.ts';
import { NameInUseError } from '@/errors/edit/NameInUseError.ts';
import { ValidationFailedError } from '@/errors/edit/ValidationFailedError.ts';
import PromptOutputFormatDialog from '@/components/dialogs/PromptOutputFormatDialog.vue';
import { Badge } from '@/components/ui/badge';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Hotkey } from '@/types/Hotkey.ts';
import HotkeyIndicator from '@/components/hotkey/HotkeyIndicator.vue';
import { VariableConfigService } from '@/services/config/VariableConfigService.ts';
import { useRoute } from 'vue-router';
import { z } from 'zod';
import { eventEmitter, type HotkeyPressPayload } from '@/lib/eventEmitter.ts';
import type { ReasoningEffort } from '@/types/ReasoningEffort.ts';
import { normalizeReasoningEffort } from '@/utils/ModelCompatibilityUtils.ts';

/* region Router */
const route = useRoute();
const routeParamsSchema = z.object({
    profileConfigId: z.string(),
    stepConfigId: z.string().optional(),
    promptConfigId: z.string().optional(),
});
/* endregion Router */

/* region Options */
defineOptions({
    beforeRouteLeave(to, from, next) {
        if (window.unsavedChanges) {
            eventEmitter.emit('showAlertDialog', {
                title: 'Unsaved changes',
                description: 'You have unsaved changes. Are you sure you want to leave?',
                cancelText: 'Discard changes',
                actionText: 'Stay',
                continueCallback: () => next(false),
                cancelCallback: () => next(),
            });
        } else {
            next();
        }
    },
});
/* endregion Options */

/* region State */
interface ComponentState {
    // Config
    profileConfigsCopy: Record<ProfileConfig['id'], ProfileConfig>;
    stepConfigsCopy: Record<StepConfig['id'], StepConfig>;
    promptConfigsCopy: Record<PromptConfig['id'], PromptConfig>;
    // UI
    activeProfileId: ProfileConfig['id'] | undefined;
    activeStepId: StepConfig['id'] | undefined;
    changesMade: boolean;
    showHighlights: boolean;
    highlightedStepConfigId: StepConfig['id'] | undefined;
    highlightedPromptConfigId: PromptConfig['id'] | undefined;
}

const state: ComponentState = reactive({
    // Config
    profileConfigsCopy: {},
    stepConfigsCopy: {},
    promptConfigsCopy: {},
    // UI
    activeProfileId: undefined,
    activeStepId: undefined,
    changesMade: false,
    showHighlights: true,
    highlightedStepConfigId: undefined,
    highlightedPromptConfigId: undefined,
});
/* endregion State */

/* region Setup */
const sessionStore = useSessionStore();

const editUpdatePageBody = useTemplateRef<HTMLDivElement>('editUpdatePageBody');
/* endregion Setup */

/* region Step mutation */
const selectStep = (id: StepConfig['id']) => {
    state.activeStepId = id;
};

const createStep = (profileId: ProfileConfig['id']): StepConfig | undefined => {
    let newStepConfigIdTemp;
    try {
        const { profileConfigs, stepConfigs, promptConfigs, newStepConfigId } =
            StepConfigService.createStep(
                state.profileConfigsCopy,
                state.stepConfigsCopy,
                state.promptConfigsCopy,
                profileId,
            );
        state.profileConfigsCopy = profileConfigs;
        state.stepConfigsCopy = stepConfigs;
        state.promptConfigsCopy = promptConfigs;
        newStepConfigIdTemp = newStepConfigId;
    } catch (error) {
        if (error instanceof EditLimitReachedError) {
            createTextToast(
                'Max steps reached',
                `A maximum of ${MAX_STEPS_PER_PROFILE} steps can be created per profile.`,
                'destructive',
                false,
            );
        } else {
            throw error;
        }
        return;
    }

    selectStep(newStepConfigIdTemp);
};

const moveStep = (
    profileId: ProfileConfig['id'],
    stepId: StepConfig['id'],
    direction: 'up' | 'down',
) => {
    const { stepConfigs } = StepConfigService.moveStep(
        state.profileConfigsCopy,
        state.stepConfigsCopy,
        profileId,
        stepId,
        direction,
    );
    state.stepConfigsCopy = stepConfigs;
};

const updateStepName = (stepId: string, target: HTMLDivElement) => {
    try {
        const { stepConfigs } = StepConfigService.updateStepName(
            state.stepConfigsCopy,
            stepId,
            target.innerText.trim(),
        );
        state.stepConfigsCopy = stepConfigs;
    } catch (error) {
        if (error instanceof NameInUseError) {
            createTextToast('Step name already in use', error.message, 'destructive', false);
        } else if (error instanceof ValidationFailedError) {
            createTextToast('Step name validation failed', error.message, 'destructive', false);
        } else {
            throw error;
        }

        // Restore original name to contenteditable div on error
        const stepConfig = state.profileConfigsCopy[stepId];
        if (stepConfig) {
            target.innerText = stepConfig.name;
        }
    }
};

const deleteStep = (id: StepConfig['id']) => {
    const { profileConfigs, stepConfigs, promptConfigs } = StepConfigService.deleteStep(
        state.profileConfigsCopy,
        state.stepConfigsCopy,
        state.promptConfigsCopy,
        id,
    );
    state.profileConfigsCopy = profileConfigs;
    state.stepConfigsCopy = stepConfigs;
    state.promptConfigsCopy = promptConfigs;

    if (id === state.activeStepId && Object.values(state.stepConfigsCopy).length > 0) {
        selectStep(Object.values(state.stepConfigsCopy)[0].id);
    }
};

/* endregion Step mutation */

/* region Prompt mutation */
const createPrompt = async (profileId: string, stepId: string) => {
    try {
        const { profileConfigs, stepConfigs, promptConfigs } = PromptConfigService.createPrompt(
            state.profileConfigsCopy,
            state.stepConfigsCopy,
            state.promptConfigsCopy,
            profileId,
            stepId,
        );
        state.profileConfigsCopy = profileConfigs;
        state.stepConfigsCopy = stepConfigs;
        state.promptConfigsCopy = promptConfigs;
    } catch (error) {
        if (error instanceof EditLimitReachedError) {
            createTextToast(
                'Max prompt reached',
                `A maximum of ${MAX_PROMPTS_PER_PROFILE} prompts can be created per profile.`,
                'destructive',
                false,
            );
        } else {
            throw error;
        }
        return;
    }

    // Wait for re-render before scrolling down
    await nextTick();

    if (editUpdatePageBody.value) {
        editUpdatePageBody.value.scrollTop = editUpdatePageBody.value.scrollHeight;
    }
};

const duplicatePrompt = async (promptConfigId: PromptConfig['id']) => {
    try {
        const { profileConfigs, stepConfigs, promptConfigs } = PromptConfigService.duplicatePrompt(
            state.profileConfigsCopy,
            state.stepConfigsCopy,
            state.promptConfigsCopy,
            promptConfigId,
        );
        state.profileConfigsCopy = profileConfigs;
        state.stepConfigsCopy = stepConfigs;
        state.promptConfigsCopy = promptConfigs;
    } catch (error) {
        if (error instanceof EditLimitReachedError) {
            createTextToast(
                'Max prompt reached',
                `A maximum of ${MAX_PROMPTS_PER_PROFILE} prompts can be created per profile.`,
                'destructive',
                false,
            );
        } else {
            throw error;
        }
        return;
    }

    // Wait for re-render before scrolling down
    await nextTick();

    if (editUpdatePageBody.value) {
        editUpdatePageBody.value.scrollTop = editUpdatePageBody.value.scrollHeight;
    }
};

const updatePromptName = (promptConfigId: PromptConfig['id'], target: HTMLDivElement) => {
    try {
        const { promptConfigs } = PromptConfigService.updatePromptName(
            state.promptConfigsCopy,
            promptConfigId,
            target.innerText.trim(),
        );
        state.promptConfigsCopy = promptConfigs;
    } catch (error) {
        if (error instanceof NameInUseError) {
            createTextToast('Prompt name already in use', error.message, 'destructive', false);
        } else if (error instanceof ValidationFailedError) {
            createTextToast('Prompt name validation failed', error.message, 'destructive', false);
        } else {
            throw error;
        }

        // Restore original name to contenteditable div on error
        const promptConfig = state.promptConfigsCopy[promptConfigId];
        if (promptConfig) {
            target.innerText = promptConfig.name;
        }
    }
};

const updatePromptRawText = (promptConfigId: PromptConfig['id'], rawText: string) => {
    const { promptConfigs } = PromptConfigService.updatePromptRawText(
        state.promptConfigsCopy,
        promptConfigId,
        rawText,
    );
    state.promptConfigsCopy = promptConfigs;
};

const updatePromptFormat = async (
    promptConfigId: PromptConfig['id'],
    format: PromptConfig['formatAs'],
) => {
    const promptConfig = state.promptConfigsCopy[promptConfigId];
    if (!promptConfig) {
        return;
    }

    if (promptConfig.outputFormatInstruction.trim().length > 0) {
        const userConfirmed = await showAlertDialogAsync({
            title: 'Reset the output instruction?',
            description: `The current output instruction is: "${truncateString(promptConfig.outputFormatInstruction, 50)}". Would you like to reset this?`,
            actionText: 'Reset',
            cancelText: 'Keep',
        });

        if (userConfirmed) {
            updatePromptOutputFormatInstruction(promptConfigId, '');
        }
    }

    const { promptConfigs } = PromptConfigService.updatePromptFormat(
        state.promptConfigsCopy,
        promptConfigId,
        format,
    );
    state.promptConfigsCopy = promptConfigs;
};

const updatePromptModelId = (promptConfigId: string, modelIdValue: PromptConfig['modelId']) => {
    const promptConfig = state.promptConfigsCopy[promptConfigId];
    if (!promptConfig) {
        return;
    }

    const sessionStore = useSessionStore();
    const aiModel = sessionStore.aiModels[modelIdValue];
    if (!aiModel) {
        return;
    }

    if (promptConfig.formatAs === 'choice' && !aiModel.supportsStructuredOutput) {
        createTextToast(
            'Model does not support structured output',
            `The model "${aiModel.name}" does not support structured output which is recommended when output format is set to "Choice". Results may be unpredictable.`,
            'warning',
            false,
        );
    }

    if (promptConfig.includeScreenCapture && !aiModel.supportsImage) {
        createTextToast(
            'Model does not support image input',
            `The model "${aiModel.name}" does not support image input which is required when screen capture is set to "Attach". Please select a different model.`,
            'destructive',
            false,
        );
        return;
    }

    const { promptConfigs } = PromptConfigService.updatePromptModelId(
        state.promptConfigsCopy,
        promptConfigId,
        modelIdValue,
    );
    state.promptConfigsCopy = promptConfigs;
};

const updatePromptOutputFormatInstruction = (
    promptConfigId: string,
    outputFormatInstruction: PromptConfig['outputFormatInstruction'],
) => {
    const { promptConfigs } = PromptConfigService.updatePromptOutputFormatInstruction(
        state.promptConfigsCopy,
        promptConfigId,
        outputFormatInstruction,
    );
    state.promptConfigsCopy = promptConfigs;
};

const updatePromptIncludeScreenCapture = (promptConfigId: string, includeScreenCapture: string) => {
    const { promptConfigs } = PromptConfigService.updatePromptIncludeScreenCapture(
        state.promptConfigsCopy,
        promptConfigId,
        includeScreenCapture === '1',
    );
    state.promptConfigsCopy = promptConfigs;
};

const reasoningEffortLabels: Record<ReasoningEffort, string> = {
    none: 'None (fastest)',
    minimal: 'Minimal',
    low: 'Low (recommended)',
    medium: 'Medium',
    high: 'High',
};

const getPromptReasoningEffort = (prompt: PromptConfig) =>
    normalizeReasoningEffort(sessionStore.aiModels[prompt.modelId], prompt.reasoningEffort);

const updatePromptReasoningEffort = (promptConfigId: string, reasoningEffort: unknown) => {
    const promptConfig = state.promptConfigsCopy[promptConfigId];
    if (!promptConfig || typeof reasoningEffort !== 'string') return;

    const aiModel = sessionStore.aiModels[promptConfig.modelId];
    if (!aiModel?.supportedReasoningEfforts.includes(reasoningEffort as ReasoningEffort)) return;

    const { promptConfigs } = PromptConfigService.updatePromptReasoningEffort(
        state.promptConfigsCopy,
        promptConfigId,
        reasoningEffort as ReasoningEffort,
    );
    state.promptConfigsCopy = promptConfigs;
};

const movePrompt = (
    stepConfigId: StepConfig['id'],
    promptConfigId: PromptConfig['id'],
    direction: 'up' | 'down',
) => {
    const { promptConfigs } = PromptConfigService.movePrompt(
        state.stepConfigsCopy,
        state.promptConfigsCopy,
        stepConfigId,
        promptConfigId,
        direction,
    );
    state.promptConfigsCopy = promptConfigs;
};

const deletePrompt = (promptConfigId: PromptConfig['id']) => {
    const { profileConfigs, stepConfigs, promptConfigs } = PromptConfigService.deletePrompt(
        state.profileConfigsCopy,
        state.stepConfigsCopy,
        state.promptConfigsCopy,
        promptConfigId,
    );
    state.profileConfigsCopy = profileConfigs;
    state.stepConfigsCopy = stepConfigs;
    state.promptConfigsCopy = promptConfigs;
};
/* endregion Prompt mutation */

/* region Vue smart suggest */
const triggers = computed((): Trigger[] => {
    const variableConfigs = VariableConfigService.getVariableConfigs(
        state.promptConfigsCopy,
        state.activeProfileId,
        ['CUSTOM', 'DEFAULT', 'PROMPT_OUTPUT', 'PROMPT_USER_INPUT'],
    );

    let items: { value: string }[] = [];
    for (const variableConfig of Object.values(variableConfigs)) {
        items = [...items, { value: `$${variableConfig.key}` }];
    }

    const sortedItems = items.sort((a, b) => a.value.localeCompare(b.value));

    return [
        {
            char: '$',
            items: sortedItems,
        },
    ];
});
/* endregion Vue smart suggest */

/* region Saving */
const cancel = () => {
    setInitialConfigState();
};

const save = async () => {
    const variableConfigs = VariableConfigService.getVariableConfigs(
        state.promptConfigsCopy,
        state.activeProfileId,
        ['CUSTOM', 'DEFAULT', 'PROMPT_OUTPUT', 'PROMPT_USER_INPUT'],
    );

    const profileValidationErrors = ProfileConfigService.validate(state.profileConfigsCopy);
    const stepValidationErrors = StepConfigService.validate(state.stepConfigsCopy);
    const promptValidationErrors = PromptConfigService.validate(
        state.promptConfigsCopy,
        variableConfigs,
    );

    const errors = [...profileValidationErrors, ...stepValidationErrors, ...promptValidationErrors];

    if (errors.length > 0) {
        createTextToast(
            'Validation failed',
            `There are ${errors.length} validation errors. Please fix them before saving: \n\n - ${errors.join('\n - ')}`,
            'destructive',
            false,
        );
        return;
    }

    const activeProfileId = state.activeProfileId;
    if (!activeProfileId) return;

    const profileConfig = state.profileConfigsCopy[activeProfileId];
    if (!profileConfig) return;

    usePromptStore().replaceProfileGraph(
        activeProfileId,
        profileConfig,
        state.stepConfigsCopy,
        state.promptConfigsCopy,
    );

    await nextTick();

    state.changesMade = false;
};
/* endregion Saving */

/* region Other */
const setInitialConfigState = async () => {
    const routeParams = routeParamsSchema.parse({
        profileConfigId: route.params.profileConfigId,
        stepConfigId: route.query.stepConfigId,
        promptConfigId: route.query.promptConfigId,
    });

    const profileConfig = ProfileConfigService.getProfileConfig(routeParams.profileConfigId);
    if (!profileConfig) {
        return router.push(ROUTES.errorNotFound);
    }

    if (routeParams.stepConfigId !== undefined) {
        state.highlightedStepConfigId = String(routeParams.stepConfigId);
    }

    if (routeParams.promptConfigId !== undefined) {
        state.highlightedPromptConfigId = String(routeParams.promptConfigId);
    }

    state.profileConfigsCopy = { [profileConfig.id]: profileConfig };
    state.stepConfigsCopy = Object.fromEntries(
        Object.entries(StepConfigService.getAllStepConfigs()).filter(
            ([, step]) => step.profileId === profileConfig.id,
        ),
    );
    state.promptConfigsCopy = Object.fromEntries(
        Object.entries(PromptConfigService.getAllPromptConfigs()).filter(
            ([, prompt]) => prompt.profileId === profileConfig.id,
        ),
    );

    state.activeProfileId = profileConfig.id;

    if (state.highlightedStepConfigId !== undefined) {
        state.showHighlights = true;
        selectStep(state.highlightedStepConfigId);
    } else {
        const profileSteps = Object.values(state.stepConfigsCopy).filter(
            (s) => s.profileId === routeParams.profileConfigId,
        );
        if (profileSteps.length > 0) {
            selectStep(profileSteps[0].id);
        }
    }

    if (state.highlightedPromptConfigId !== undefined) {
        state.showHighlights = true;
        setTimeout(() => {
            state.showHighlights = false;
        }, 2000);
    }

    await nextTick();

    // Scroll prompt into view
    if (state.highlightedPromptConfigId !== undefined && editUpdatePageBody.value) {
        const targetEl = document.getElementById(`prompt-${state.highlightedPromptConfigId}`);
        if (targetEl) {
            editUpdatePageBody.value.scrollTop = targetEl.getBoundingClientRect().top - 150;
        }
    }

    state.changesMade = false;
};

const onHotkeyPress = (payload: HotkeyPressPayload) => {
    if (payload.hotkey === Hotkey.Left) {
        router.push({ name: ROUTES.editIndex });
    }
};

const beforeUnloadListener = (event: BeforeUnloadEvent) => {
    if (window.unsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
    }
};
/* endregion Other */

/* region Computed */
const activeProfileConfig = computed((): ProfileConfig | undefined => {
    if (!state.activeProfileId) {
        return undefined;
    }

    return state.profileConfigsCopy[state.activeProfileId];
});

const activeStepConfig = computed((): StepConfig | undefined => {
    if (!state.activeStepId) {
        return undefined;
    }

    return state.stepConfigsCopy[state.activeStepId];
});

const sortedActiveSteps = computed((): StepConfig[] => {
    if (!activeProfileConfig.value) {
        return [];
    }

    return Object.values(state.stepConfigsCopy)
        .filter((s) => s.profileId === activeProfileConfig.value?.id)
        .sort((a, b) => a.order - b.order);
});

const sortedActivePrompts = computed((): PromptConfig[] => {
    return Object.values(state.promptConfigsCopy)
        .filter((p) => p.stepId === state.activeStepId)
        .sort((a, b) => a.order - b.order);
});
/* endregion Computed */

/* region Lifecycle */
watch(
    () => state.changesMade,
    (newValue) => {
        window.unsavedChanges = newValue;
    },
);

watch(
    () => [state.profileConfigsCopy, state.stepConfigsCopy, state.promptConfigsCopy],
    () => {
        state.changesMade = true;
    },
    { deep: true },
);

onMounted(() => {
    setInitialConfigState();
    eventEmitter.addListener('hotkeyPress', onHotkeyPress);
    window.addEventListener('beforeunload', beforeUnloadListener);
});

onUnmounted(() => {
    eventEmitter.removeListener('hotkeyPress', onHotkeyPress);
    window.removeEventListener('beforeunload', beforeUnloadListener);
});
/* endregion Lifecycle */
</script>

<template>
    <div
        class="flex-shrink flex-grow overflow-y-scroll"
        ref="editUpdatePageBody"
        v-if="activeProfileConfig"
    >
        <NavHeader>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink as-child class="flex gap-x-1.5">
                            <RouterLink :to="ROUTES.index"> Home </RouterLink>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink as-child class="flex gap-x-1.5">
                            <RouterLink :to="ROUTES.editIndex">
                                Profiles
                                <HotkeyIndicator :hotkey="Hotkey.Left" />
                            </RouterLink>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>{{ activeProfileConfig.name ?? '' }}</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
        </NavHeader>

        <template v-if="activeProfileConfig">
            <div class="flex-1 flex-shrink flex-grow flex-col px-2">
                <!-- Profile bar -->
                <Card class="mb-2">
                    <CardContent class="p-2 px-4">
                        <div class="flex items-center justify-between">
                            <div class="flex">
                                <div class="text-base font-semibold">
                                    {{ activeProfileConfig.name }}
                                </div>
                                <span v-if="state.activeStepId" class="mx-1 text-muted-foreground"
                                    >></span
                                >
                                <div
                                    contenteditable
                                    @focusout="
                                        (e) =>
                                            state.activeStepId
                                                ? updateStepName(
                                                      state.activeStepId,
                                                      e.target as HTMLDivElement,
                                                  )
                                                : null
                                    "
                                    @keydown.enter.prevent="
                                        (($event as KeyboardEvent).target as HTMLElement).blur()
                                    "
                                    class="text-base font-semibold"
                                    :class="{
                                        'rounded border border-red-700/80 px-1': state.activeStepId
                                            ? state.stepConfigsCopy[state.activeStepId]?.name ===
                                              'Enter Step Name'
                                            : false,
                                    }"
                                >
                                    {{
                                        state.activeStepId
                                            ? state.stepConfigsCopy[state.activeStepId]?.name
                                            : ''
                                    }}
                                </div>
                            </div>
                            <div class="flex gap-x-2">
                                <AvailableVariablesDialog
                                    :prompt-configs="state.promptConfigsCopy"
                                    :profile-config-id="activeProfileConfig.id"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <!-- Prompts -->
                <Card
                    v-for="(prompt, index) in sortedActivePrompts"
                    :id="`prompt-${prompt.id}`"
                    :key="prompt.id"
                    class="mb-2"
                    :class="[
                        'transition-all duration-1000',
                        state.showHighlights && state.highlightedPromptConfigId === prompt.id
                            ? 'border border-green-500'
                            : 'border-transparent',
                    ]"
                >
                    <CardHeader class="p-2 px-4">
                        <CardTitle>
                            <div class="flex justify-between">
                                <div class="flex items-center justify-between text-base">
                                    <span class="me-1 text-muted-foreground">
                                        [Prompt {{ index + 1 }}]
                                    </span>
                                    <div
                                        contenteditable
                                        @focusout="
                                            (e) =>
                                                updatePromptName(
                                                    prompt.id,
                                                    e.target as HTMLDivElement,
                                                )
                                        "
                                        class="min-w-[50px]"
                                        :class="{
                                            'rounded border border-red-700/80 px-1':
                                                prompt.name === 'Enter Prompt Name',
                                        }"
                                        @keydown.enter.prevent="
                                            (($event as KeyboardEvent).target as HTMLElement).blur()
                                        "
                                    >
                                        {{ prompt.name }}
                                    </div>
                                </div>
                                <div class="flex gap-x-2">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost">
                                                <EllipsisIcon />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent>
                                            <DropdownMenuItem
                                                @click="
                                                    state.activeStepId
                                                        ? movePrompt(
                                                              state.activeStepId,
                                                              prompt.id,
                                                              'up',
                                                          )
                                                        : null
                                                "
                                                :disabled="prompt.order === 0"
                                                ><ArrowUpIcon />Move prompt up</DropdownMenuItem
                                            >
                                            <DropdownMenuItem
                                                @click="
                                                    state.activeStepId
                                                        ? movePrompt(
                                                              state.activeStepId,
                                                              prompt.id,
                                                              'down',
                                                          )
                                                        : null
                                                "
                                                :disabled="
                                                    prompt.order >= sortedActivePrompts.length - 1
                                                "
                                                ><ArrowDownIcon />Move prompt down</DropdownMenuItem
                                            >
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem @click="duplicatePrompt(prompt.id)"
                                                ><CopyIcon />Duplicate prompt</DropdownMenuItem
                                            >
                                            <DropdownMenuItem @click="deletePrompt(prompt.id)"
                                                ><Trash2Icon />Delete prompt</DropdownMenuItem
                                            >
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent class="p-2 px-4">
                        <div class="gap-y-4 sm:flex sm:gap-x-4">
                            <div class="flex w-full flex-col gap-y-2">
                                <Label>Prompt</Label>
                                <SmartSuggest :triggers="triggers">
                                    <Textarea
                                        :model-value="prompt.rawText"
                                        @update:model-value="
                                            updatePromptRawText(prompt.id, $event as FormatOption)
                                        "
                                        class="custom-scrollbar text-md h-[240px] font-mono dark:bg-gray-900"
                                        spellcheck="false"
                                    />
                                </SmartSuggest>
                            </div>
                            <div class="mt-4 flex w-full flex-col gap-y-2 sm:mt-0 sm:w-96">
                                <div class="mb-2 flex flex-col gap-y-2">
                                    <Label>Output format</Label>
                                    <Select
                                        :model-value="prompt.formatAs"
                                        @update:model-value="
                                            updatePromptFormat(prompt.id, $event as FormatOption)
                                        "
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a format" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem
                                                v-for="formatOption in FormatOptions"
                                                :key="formatOption.value"
                                                :value="formatOption.value"
                                            >
                                                <div class="flex items-center">
                                                    <component
                                                        :is="formatOption.icon"
                                                        class="me-2 size-3.5"
                                                    />
                                                    {{ formatOption.label }}
                                                </div>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <PromptOutputFormatDialog
                                        :outputFormatInstruction="prompt.outputFormatInstruction"
                                        :showChoiceFormatWarning="prompt.formatAs === 'choice'"
                                        :prompt-configs="state.promptConfigsCopy"
                                        :triggers="triggers"
                                        :disabled="false"
                                        @change="
                                            updatePromptOutputFormatInstruction(prompt.id, $event)
                                        "
                                    />
                                </div>

                                <div class="mb-2 flex flex-col gap-y-2">
                                    <Label class="flex items-center">
                                        Screen capture
                                        <TooltipProvider :delay-duration="150">
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <InfoIcon class="ms-1 inline h-3.5 w-3.5" />
                                                </TooltipTrigger>
                                                <TooltipContent side="left">
                                                    <p class="max-w-40">
                                                        Setting this to "Don't attach" will allow
                                                        the model to run faster, but it won't have
                                                        access to your screen capture. If you want
                                                        the model to be able to view your screen
                                                        capture, set this to "Attach".
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </Label>
                                    <Select
                                        :model-value="prompt.includeScreenCapture ? '1' : '0'"
                                        @update:model-value="
                                            updatePromptIncludeScreenCapture(prompt.id, $event)
                                        "
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a format" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem :value="'1'">
                                                <div class="flex items-center">Attach</div>
                                            </SelectItem>
                                            <SelectItem :value="'0'">
                                                <div class="flex items-center">
                                                    Don't attach (faster)
                                                </div>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div class="mb-2 flex flex-col gap-y-2">
                                    <Label>AI Model</Label>
                                    <Popover>
                                        <PopoverTrigger as-child>
                                            <Button
                                                variant="outline"
                                                role="combobox"
                                                class="justify-between"
                                            >
                                                {{ sessionStore.aiModels[prompt.modelId]?.name }}
                                                <ChevronDownIcon
                                                    class="ml-2 h-4 w-4 shrink-0 opacity-50"
                                                />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent class="w-[520px] p-0">
                                            <Command>
                                                <CommandInput placeholder="Search model..." />
                                                <CommandEmpty>No models found.</CommandEmpty>
                                                <CommandList>
                                                    <!-- Recommended models -->
                                                    <CommandGroup heading="Recommended models">
                                                        <CommandItem
                                                            v-for="model in Object.values(
                                                                sessionStore.aiModels,
                                                            ).filter((m) => m.isRecommended)"
                                                            :value="model.id"
                                                            :key="model.id"
                                                            @select="
                                                                updatePromptModelId(
                                                                    prompt.id,
                                                                    model.id,
                                                                )
                                                            "
                                                        >
                                                            <CheckIcon
                                                                :class="
                                                                    cn(
                                                                        'mr-2 h-4 w-4',
                                                                        prompt.modelId === model.id
                                                                            ? 'opacity-100'
                                                                            : 'opacity-0',
                                                                    )
                                                                "
                                                            />
                                                            {{ model.name }}
                                                            <Badge
                                                                class="ms-1 rounded-sm px-1"
                                                                variant="secondary"
                                                                v-if="
                                                                    model.supportsStructuredOutput
                                                                "
                                                                >Structured output</Badge
                                                            >
                                                            <Badge
                                                                class="ms-1 rounded-sm px-1"
                                                                variant="secondary"
                                                                v-if="model.supportsImage"
                                                                >Image</Badge
                                                            >
                                                            <Badge
                                                                class="ms-1 rounded-sm px-1"
                                                                variant="secondary"
                                                                v-if="!model.supportsImage"
                                                                >Text only</Badge
                                                            >
                                                        </CommandItem>
                                                    </CommandGroup>
                                                    <!-- Other models -->
                                                    <CommandGroup heading="Other models">
                                                        <CommandItem
                                                            v-for="model in Object.values(
                                                                sessionStore.aiModels,
                                                            ).filter((m) => !m.isRecommended)"
                                                            :value="model.id"
                                                            :key="model.id"
                                                            @select="
                                                                updatePromptModelId(
                                                                    prompt.id,
                                                                    model.id,
                                                                )
                                                            "
                                                        >
                                                            <CheckIcon
                                                                :class="
                                                                    cn(
                                                                        'mr-2 h-4 w-4',
                                                                        prompt.modelId === model.id
                                                                            ? 'opacity-100'
                                                                            : 'opacity-0',
                                                                    )
                                                                "
                                                            />
                                                            {{ model.name }}
                                                            <Badge
                                                                class="ms-1 rounded-sm px-1"
                                                                variant="secondary"
                                                                v-if="
                                                                    model.supportsStructuredOutput
                                                                "
                                                                >Structured output</Badge
                                                            >
                                                            <Badge
                                                                class="ms-1 rounded-sm px-1"
                                                                variant="secondary"
                                                                v-if="model.supportsImage"
                                                                >Image</Badge
                                                            >
                                                            <Badge
                                                                class="ms-1 rounded-sm px-1"
                                                                variant="secondary"
                                                                v-if="!model.supportsImage"
                                                                >Text</Badge
                                                            >
                                                        </CommandItem>
                                                    </CommandGroup>
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                <div
                                    v-if="
                                        sessionStore.aiModels[prompt.modelId]
                                            ?.supportedReasoningEfforts.length
                                    "
                                    class="mb-2 flex flex-col gap-y-2"
                                >
                                    <Label class="flex items-center">
                                        Reasoning effort
                                        <TooltipProvider :delay-duration="150">
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <InfoIcon class="ms-1 inline h-3.5 w-3.5" />
                                                </TooltipTrigger>
                                                <TooltipContent side="left">
                                                    <p class="max-w-48">
                                                        More reasoning can improve difficult
                                                        problems, but usually takes longer. Use None
                                                        for the fastest response.
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </Label>
                                    <Select
                                        :model-value="getPromptReasoningEffort(prompt)"
                                        @update:model-value="
                                            updatePromptReasoningEffort(prompt.id, $event)
                                        "
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select reasoning effort" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem
                                                v-for="effort in sessionStore.aiModels[
                                                    prompt.modelId
                                                ].supportedReasoningEfforts"
                                                :key="effort"
                                                :value="effort"
                                            >
                                                {{ reasoningEffortLabels[effort] }}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div class="mb-2 flex flex-col gap-y-2">
                                    <template v-if="prompt.formatAs !== 'choice'">
                                        <Label class="flex items-center">
                                            Output variable
                                            <TooltipProvider :delay-duration="150">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <InfoIcon class="ms-1 inline h-3.5 w-3.5" />
                                                    </TooltipTrigger>
                                                    <TooltipContent side="left">
                                                        <p class="max-w-40">
                                                            The completed prompt output will be
                                                            stored in this variable. You can use
                                                            this variable in other prompts.
                                                        </p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </Label>
                                        <div class="flex w-full items-center">
                                            <Input
                                                type="text"
                                                :default-value="
                                                    VariableConfigService.getPromptOutputVariableKey(
                                                        prompt.name,
                                                    )
                                                "
                                                :value="
                                                    VariableConfigService.getPromptOutputVariableKey(
                                                        prompt.name,
                                                    )
                                                "
                                                disabled
                                                class="rounded-e-none"
                                            />
                                            <TooltipProvider :delay-duration="150">
                                                <Tooltip>
                                                    <TooltipTrigger as-child>
                                                        <Button
                                                            @click="
                                                                copy(
                                                                    `$${VariableConfigService.getPromptOutputVariableKey(
                                                                        prompt.name,
                                                                    )}`,
                                                                )
                                                            "
                                                            type="button"
                                                            variant="secondary"
                                                            class="h-[40px] rounded-s-none"
                                                        >
                                                            <ClipboardCopyIcon />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Copy variable key</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <Label class="flex items-center">
                                            Input variable
                                            <TooltipProvider :delay-duration="150">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <InfoIcon class="ms-1 inline h-3.5 w-3.5" />
                                                    </TooltipTrigger>
                                                    <TooltipContent side="left">
                                                        <p class="max-w-40">
                                                            The selected choice (title) will be
                                                            stored in this variable. You can use
                                                            this variable in other prompts.
                                                        </p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </Label>
                                        <div class="flex w-full max-w-sm items-center">
                                            <Input
                                                type="text"
                                                :value="
                                                    VariableConfigService.getPromptUserInputVariableKey(
                                                        prompt.name,
                                                    )
                                                "
                                                :default-value="
                                                    VariableConfigService.getPromptUserInputVariableKey(
                                                        prompt.name,
                                                    )
                                                "
                                                disabled
                                                class="rounded-e-none"
                                            />
                                            <TooltipProvider :delay-duration="150">
                                                <Tooltip>
                                                    <TooltipTrigger as-child>
                                                        <Button
                                                            @click="
                                                                copy(
                                                                    `$${VariableConfigService.getPromptUserInputVariableKey(
                                                                        prompt.name,
                                                                    )}`,
                                                                )
                                                            "
                                                            type="button"
                                                            variant="secondary"
                                                            class="h-[40px] rounded-s-none"
                                                        >
                                                            <ClipboardCopyIcon />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Copy variable key</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <div class="mb-4 flex w-full justify-end">
                    <Button
                        variant="secondary"
                        size="sm"
                        @click="
                            activeStepConfig
                                ? createPrompt(activeProfileConfig.id, activeStepConfig.id)
                                : null
                        "
                        :disabled="!activeStepConfig"
                    >
                        <PlusIcon />
                        Add prompt
                    </Button>
                </div>
            </div>
        </template>
    </div>

    <template v-if="activeProfileConfig">
        <div class="h-[37px] flex-shrink-0">
            <Card class="rounded-none border-b-0">
                <CardContent class="flex p-0 text-sm text-muted-foreground">
                    <div class="flex w-full justify-between">
                        <div class="flex h-[37px] max-w-[calc(100vw-172px)] overflow-hidden">
                            <template v-for="(step, index) in sortedActiveSteps" :key="step.id">
                                <div
                                    class="flex items-center gap-1 border-e p-2 px-3 hover:text-foreground"
                                    :class="{
                                        'text-foreground': step.id === state.activeStepId,
                                    }"
                                >
                                    <div
                                        class="inline overflow-hidden text-ellipsis whitespace-nowrap"
                                        @click="selectStep(step.id)"
                                    >
                                        <span class="text-muted-foreground">
                                            {{ index + 1 + '. ' }}
                                        </span>
                                        <span
                                            class="hidden max-w-[20vw] overflow-hidden overflow-ellipsis whitespace-nowrap sm:inline"
                                        >
                                            {{ step.name }}
                                        </span>
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger>
                                            <ChevronUpIcon class="size-3.5" />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start">
                                            <DropdownMenuItem
                                                @click="
                                                    moveStep(activeProfileConfig.id, step.id, 'up')
                                                "
                                                ><ArrowLeftIcon />Move step left</DropdownMenuItem
                                            >
                                            <DropdownMenuItem
                                                @click="
                                                    moveStep(
                                                        activeProfileConfig.id,
                                                        step.id,
                                                        'down',
                                                    )
                                                "
                                                ><ArrowRightIcon />Move step right</DropdownMenuItem
                                            >
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem @click="deleteStep(step.id)"
                                                ><Trash2Icon />Delete step</DropdownMenuItem
                                            >
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </template>
                            <TooltipProvider :delay-duration="150">
                                <Tooltip>
                                    <TooltipTrigger>
                                        <Button
                                            variant="ghost"
                                            class="flex items-center gap-1 rounded-none border-e p-2 px-3 dark:hover:bg-zinc-900"
                                            @click="createStep(activeProfileConfig.id)"
                                        >
                                            <PlusIcon />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent> Add step </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        <div class="flex items-center">
                            <Button
                                variant="destructive"
                                class="rounded-none"
                                @click="cancel"
                                :disabled="!state.changesMade"
                            >
                                <XIcon />
                                <span class="hidden sm:inline">Cancel</span>
                            </Button>
                            <Button
                                variant="success"
                                class="rounded-none"
                                @click="save"
                                :disabled="!state.changesMade"
                            >
                                <SaveIcon />
                                <span class="hidden sm:inline">Save</span>
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    </template>
</template>

<style>
.smart-suggest-dropdown {
    @apply z-50 rounded border border-white bg-zinc-900 p-2 font-mono;
    height: fit-content !important;
    width: fit-content !important;
}

.smart-suggest-item:not(:last-child) {
    @apply mb-2;
}
</style>
